import { useEffect, useState } from "react";
import { Outlet, NavLink, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { NotificationBell } from "@/components/NotificationBell";
import { ShieldCheck as ShieldIcon, Boxes as BoxesIcon } from "lucide-react";
import powercomLogo from "@/assets/powercom-logo.webp";
import { Button } from "@/components/ui/button";
import { enablePush, disablePush, isPushEnabled, pushSupported } from "@/lib/push";
import { toast } from "sonner";
import {
  HardHat,
  LayoutDashboard,
  PlusCircle,
  ListChecks,
  Truck,
  Users as UsersIcon,
  ClipboardCheck,
  LogOut,
  Menu,
  X,
  Bell,
  BellOff,
  Download,
  CreditCard,
  Settings,
  History,
  BellRing,
  Tags,
  UserCircle,
  Home,
  Briefcase,
  Package,
  ListTree,
  FilePlus,
  BarChart3,
  Clock,
  Ruler,
  Activity,
  ScrollText,
  Gauge,
} from "lucide-react";

const ROLE_LABELS = {
  utilizzatore: "Utilizzatore",
  approvatore: "Approvatore",
  admin: "Amministratore",
};

const ALL = ["utilizzatore", "approvatore", "admin"];
const SECTIONS = [
  { title: null, items: [{ to: "/", label: "Home", icon: Home, end: true, roles: ALL }] },
  { title: "Carburante", items: [
    { to: "/carburante", label: "Panoramica", icon: LayoutDashboard, roles: ALL },
    { to: "/nuova-richiesta", label: "Nuova richiesta", icon: PlusCircle, roles: ALL },
    { to: "/le-mie-richieste", label: "Le mie richieste", icon: ListChecks, roles: ["utilizzatore"] },
    { to: "/richieste", label: "Richieste da approvare", icon: ClipboardCheck, roles: ["approvatore", "admin"] },
    { to: "/storico", label: "Storico richieste", icon: History, roles: ["approvatore", "admin"] },
    { to: "/consumi", label: "Consumi", icon: Gauge, roles: ["approvatore", "admin"] },
    { to: "/carte", label: "Carte carburante", icon: CreditCard, roles: ["admin"] },
  ] },
  { title: "Rapportini", items: [
    { to: "/nuovo-rapportino", label: "Nuovo rapportino", icon: FilePlus, roles: ["utilizzatore"] },
    { to: "/miei-rapportini", label: "I miei rapportini", icon: ListChecks, roles: ["utilizzatore"] },
    { to: "/rapportini", label: "Rapportini", icon: ClipboardCheck, roles: ["approvatore", "admin"] },
    { to: "/rapportini-dashboard", label: "Dashboard", icon: BarChart3, roles: ALL },
  ] },
  { title: "Check-in", items: [
    { to: "/check-in", label: "Check-in cantiere", icon: ShieldIcon, roles: ["utilizzatore"] },
    { to: "/check-in-list", label: "Check-in cantiere", icon: ShieldIcon, roles: ["approvatore", "admin"] },
  ] },
  { title: "Anagrafiche", items: [
    { to: "/commesse", label: "Commesse", icon: Briefcase, roles: ["admin"] },
    { to: "/listini", label: "Listini", icon: ListTree, roles: ["admin"] },
    { to: "/tipi-ore", label: "Tipi di ore", icon: Clock, roles: ["admin"] },
    { to: "/unita-misura", label: "Unità di misura", icon: Ruler, roles: ["admin"] },
    { to: "/tipi-attivita", label: "Tipi di attività", icon: Activity, roles: ["admin"] },
    { to: "/attrezzature", label: "Attrezzature", icon: BoxesIcon, roles: ["admin"] },
    { to: "/mezzi", label: "Mezzi", icon: Truck, roles: ["admin"] },
    { to: "/anagrafiche", label: "Tipi mezzo & gestori", icon: Tags, roles: ["admin"] },
    { to: "/utenti", label: "Dipendenti & Carte", icon: UsersIcon, roles: ["admin"] },
  ] },
  { title: "Impostazioni", items: [
    { to: "/impostazioni", label: "Impostazioni", icon: Settings, roles: ["admin"] },
    { to: "/log", label: "Log", icon: ScrollText, roles: ["admin"] },
  ] },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notif, setNotif] = useState(false);
  const [busy, setBusy] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [dismissedBanner, setDismissedBanner] = useState(false);

  useEffect(() => {
    isPushEnabled().then(setNotif).catch(() => {});
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const toggleNotif = async () => {
    setBusy(true);
    try {
      if (notif) {
        await disablePush();
        setNotif(false);
        toast.success("Notifiche disattivate");
      } else {
        await enablePush();
        setNotif(true);
        toast.success("Notifiche attivate su questo dispositivo");
      }
    } catch (e) {
      toast.error(e.message || "Impossibile attivare le notifiche");
    } finally {
      setBusy(false);
    }
  };

  const handleInstall = async () => {
    if (!installPrompt) {
      toast.info("Per installare: apri il menu del browser e scegli 'Aggiungi a schermata Home'.");
      return;
    }
    installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  };

  const links = null;
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isStandalone =
    window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

  return (
    <div className="flex min-h-screen w-full overflow-x-hidden bg-slate-50">
      <aside
        data-testid="sidebar"
        className={`fixed inset-y-0 left-0 z-40 flex w-64 transform flex-col border-r border-slate-200 bg-white transition-transform duration-200 md:static md:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex h-16 shrink-0 items-center gap-2 border-b border-slate-200 px-6">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-[#002FA7]">
            <HardHat className="h-5 w-5 text-white" />
          </div>
          <img src={powercomLogo} alt="Powercom" className="h-5 w-auto" data-testid="sidebar-logo" />
          <button className="ml-auto md:hidden" onClick={() => setOpen(false)} data-testid="close-sidebar-btn">
            <X className="h-5 w-5 text-slate-500" />
          </button>
        </div>
        <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto p-4">
          {SECTIONS.map((sec, si) => {
            const secLinks = sec.items.filter((l) => l.roles.includes(user?.role));
            if (!secLinks.length) return null;
            return (
              <div key={si} className="mb-1">
                {sec.title && (
                  <p className="px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{sec.title}</p>
                )}
                {secLinks.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    end={l.end}
                    onClick={() => setOpen(false)}
                    data-testid={`nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                        isActive
                          ? "bg-[#002FA7] text-white"
                          : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                      }`
                    }
                  >
                    <l.icon className="h-4 w-4" />
                    {l.label}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>

        <div className="shrink-0 space-y-2 border-t border-slate-200 p-3">
          {installPrompt && (
            <Button variant="outline" size="sm" onClick={handleInstall} data-testid="install-app-btn" className="w-full rounded-md">
              <Download className="mr-2 h-4 w-4" /> Installa app
            </Button>
          )}
          {pushSupported() && (
            <Button
              variant={notif ? "default" : "outline"}
              size="sm"
              onClick={toggleNotif}
              disabled={busy}
              data-testid="toggle-notifications-btn"
              className={`w-full rounded-md ${notif ? "bg-[#002FA7] text-white hover:bg-[#00227A]" : ""}`}
            >
              {notif ? <Bell className="mr-2 h-4 w-4" /> : <BellOff className="mr-2 h-4 w-4" />}
              {notif ? "Notifiche attive" : "Attiva notifiche"}
            </Button>
          )}
          <div className="flex items-center gap-2">
            <Link to="/profilo" onClick={() => setOpen(false)} data-testid="header-profile-link" className="flex min-w-0 flex-1 items-center gap-2 rounded-md p-1.5 transition-colors hover:bg-slate-100">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 font-heading text-sm font-bold text-[#002FA7]">
                {(user?.name || "?").charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-slate-900" data-testid="header-user-name">{user?.name}</p>
                <p className="truncate text-xs text-slate-400">{ROLE_LABELS[user?.role] || user?.role}</p>
              </div>
            </Link>
            <NotificationBell />
            <Button variant="outline" size="icon" onClick={handleLogout} data-testid="logout-btn" className="h-9 w-9 shrink-0 rounded-md" title="Esci">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>

      {open && <div className="fixed inset-0 z-30 bg-black/30 md:hidden" onClick={() => setOpen(false)} />}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 items-center gap-3 border-b border-slate-200 bg-white px-4 md:hidden">
          <button onClick={() => setOpen(true)} data-testid="open-sidebar-btn">
            <Menu className="h-6 w-6 text-slate-700" />
          </button>
          <img src={powercomLogo} alt="Powercom" className="h-5 w-auto" />
          <div className="ml-auto"><NotificationBell /></div>
        </header>
        <main className="min-w-0 flex-1 p-4 md:p-8">
          {pushSupported() &&
            !notif &&
            !dismissedBanner &&
            ["utilizzatore", "approvatore"].includes(user?.role) && (
              <div
                data-testid="notif-onboarding-banner"
                className="mb-6 flex flex-wrap items-center gap-4 rounded-lg border border-[#002FA7]/20 bg-blue-50 p-4"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-[#002FA7] text-white">
                  <BellRing className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-heading font-bold text-slate-900">Attiva le notifiche</p>
                  <p className="text-sm text-slate-600">
                    Ricevi un avviso sul telefono appena la tua richiesta viene approvata.
                    {isIOS && !isStandalone && (
                      <span className="mt-1 block text-slate-500">
                        Su iPhone: tocca <b>Condividi</b> e poi <b>Aggiungi a Home</b> per installare l'app, poi torna qui e attiva le notifiche.
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {installPrompt && (
                    <Button variant="outline" size="sm" onClick={handleInstall} data-testid="banner-install-btn" className="rounded-md">
                      <Download className="mr-2 h-4 w-4" /> Installa app
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={toggleNotif}
                    disabled={busy}
                    data-testid="banner-enable-notif-btn"
                    className="rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]"
                  >
                    <BellRing className="mr-2 h-4 w-4" /> Attiva notifiche
                  </Button>
                  <button
                    onClick={() => setDismissedBanner(true)}
                    data-testid="banner-dismiss-btn"
                    className="text-slate-400 transition-colors hover:text-slate-600"
                    aria-label="Chiudi"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          <Outlet />
        </main>
      </div>
    </div>
  );
}
